import { useEffect, useMemo, useState } from "react";

import {
  AccountBalanceOutlined,
  ApartmentOutlined,
  AttachFileOutlined,
  CodeRounded,
  HistoryOutlined,
  LocationOnOutlined,
  PersonOutline,
  PlaceOutlined,
  TextSnippetOutlined,
} from "@mui/icons-material";
import { Box, useMediaQuery, useTheme } from "@mui/material";

import { isEmpty, isNil, size } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";

import ToggleButton from "../../_src_shared/buttons/ToggleButton";

import { formatDate } from "../../_src_shared/utils/functions/formatDate";
import { formatPhone } from "../../_src_shared/utils/functions/formatPhone";
import { formatSsn } from "../../_src_shared/utils/functions/formatSsn";
import JsonPreview from "../../components/common/JsonPreview";
import Screen from "../../components/layout/Screen";
import ScreenContent from "../../components/layout/ScreenContent";
import TitledHeader from "../../components/layout/TitledHeader";
import EnhancedTabSwitcher from "../../components/navigation/EnhancedTabSwitcher";
import SidebarEditProfileDataIconButton from "../../components/sidebars/DashboardSidebar/buttons/SidebarEditProfileDataIconButton";
import InfoGrid from "../../components/ui/data/InfoGrid";
import PrimaryContentCard from "../../components/ui/PrimaryContentCard";
import { profileSet } from "../../redux/actions/profileActions";
import ProfileInputRow from "./components/ProfileInputRow";
import ProfileSection from "./components/ProfileSection";
import ProfileSectionContentCard from "./components/ProfileSectionContentCard";
import AssociatedEntitiesSection from "./sections/AssociatedEntitiesSection";
import BorrowerExperienceSection from "./sections/BorrowerExperienceSection";
import ProfileDocumentLibrary from "./sections/ProfileDocumentLibrary";
import ProfileBorrowerInfomationSection from "./sections/ProfileBorrowerInfomationSection";
import ProfileBankInformationSection from "./sections/ProfileBankInformationSection";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

const TABS = {
  MY_INFORMATION: "My Information",
  PRIMARY_ADDRESS: "Primary Address",
  ASSOCIATED_ENTITIES: "Associated LLC's & Entities",
  BORROWER_EXPERIENCE: "Experience",
  DOCUMENT_LIBRARY: "Document Library",
  BANK_INFORMATION: "Bank Information",
};

const ProfileScreen = () => {
  const profile = useSelector((state) => state.profile);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const location = useLocation();
  const entities = useSelector((state) => state.profile?.associatedEntities);
  const experience = useSelector((state) => state.profile?.validatedExperience);
  const documents = useSelector((state) => state.profile?.borrowerDocuments);
  const bankAccounts = useSelector((state) => state.profile?.bankAccounts);

  const currentPath = location.pathname.split("/profile")[1] || "/";

  const TABS_LIST = [
    {
      value: "information",
      label: "My Information",
      mobileLabel: "Details",
      icon: <TextSnippetOutlined className="thin" />,
      path: "/", // Maps to /profile/
    },
    {
      value: "entities",
      label: "Associated LLC's & Entities",
      mobileLabel: "Entities",
      icon: <ApartmentOutlined className="thin" />,
      path: "/entities", // Maps to /profile/entities
      badge: {
        show: isEmpty(entities),
        variant: "dot",
        color: "error",
      },
    },
    {
      value: "experience",
      label: "Borrower Experience",
      mobileLabel: "Experience",
      icon: <HistoryOutlined className="thin" />,
      path: "/experience", // Maps to /profile/experience
      badge: {
        show: isEmpty(experience),
        variant: "dot",
        color: "error",
      },
    },
    {
      value: "files",
      label: `Document Library (${size(documents)})`,
      mobileLabel: "Documents",
      icon: <AttachFileOutlined className="thin" />,
      path: "/files", // Maps to /profile/files
      badge: {
        show: isEmpty(documents),
        variant: "dot",
        color: "error",
      },
    },
    {
      value: "banks",
      label: "Bank Information",
      mobileLabel: "Bank",
      icon: <AccountBalanceOutlined className="thin" />,
      path: "/banks", // Maps to /profile/banks
      badge: {
        show: isEmpty(bankAccounts),
        variant: "dot",
        color: "error",
      },
    },
  ];

  // useEffect(() => {
  //   if (isNil(profile)) {
  //     setTimeout(() => {
  //       dispatch(profileSet(profile));
  //     }, 100);
  //   } else {
  //     setInitialValues(profile);
  //   }
  // }, [profile]);

  // Don't render form until profile data is loaded
  if (!profile) {
    return (
      <Screen>
        <TitledHeader title={"Profile"} />
        <ScreenContent>
          <Txt>Loading profile...</Txt>
        </ScreenContent>
      </Screen>
    );
  }

  return (
    <Screen background="#ffffff">
      <TitledHeader title={"Profile"} />
      <Box
        sx={{
          p: 0,
          height: "100%",
          width: "100%",
          overflow: "hidden",
          display: "flex",
          backgroundColor: "#f5f8fa",
          flexDirection: isMobile ? "column" : "row",
          position: "relative",
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            py: 1.4,
            overflowX: "hidden",
            overflowY: "auto",
            height: isMobile ? "calc(100% - 80px)" : "100%",
            minHeight: 0,
          }}
        >
          <ScreenContent
            maxWidth={"lg"}
            sx={{
              width: "100%",
              pb: 14,
              px: isMobile ? 0 : 5,
              margin: "0px auto",
            }}
          >
            <PrimaryContentCard title={"My Profile"}>
              <EnhancedTabSwitcher
                value={currentPath}
                tabs={TABS_LIST}
                useRouting // Enable routing
                basePath="/profile" // Base path for all tabs
                orientation="horizontal"
                wrap
              />

              <Routes>
                <Route
                  path="/"
                  element={<ProfileBorrowerInfomationSection />}
                />
                <Route
                  path="/entities"
                  element={<AssociatedEntitiesSection />}
                />
                <Route
                  path="/experience"
                  element={<BorrowerExperienceSection />}
                />
                <Route path="/files" element={<ProfileDocumentLibrary />} />
                <Route
                  path="/banks"
                  element={<ProfileBankInformationSection />}
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </PrimaryContentCard>
          </ScreenContent>
        </Box>
      </Box>
    </Screen>
  );
};

export default ProfileScreen;
